/**
 * Knex implementation of ITreeRepository.
 *
 * Extends KnexCollectionRepository with tree-specific operations.
 * Assumes a `parent_id` column (configurable) in the table.
 * All traversals use iterative BFS — no recursive SQL, fully portable.
 */

import type { Knex } from 'knex'
import type { ITreeRepository } from '../types.js'
import { KnexCollectionRepository, type KnexCollectionRepoOptions } from './collection.repo.js'
import { handleDbError } from '../../../db/knex-adapter.js'

export interface KnexTreeRepoOptions extends KnexCollectionRepoOptions {
  /** Column name for parent reference. Default: 'parent_id'. */
  parentField?: string
}

export class KnexTreeRepository<T extends Record<string, unknown> = Record<string, unknown>>
  extends KnexCollectionRepository<T>
  implements ITreeRepository<T>
{
  protected readonly parentField: string

  constructor(knex: Knex, table: string, options: KnexTreeRepoOptions = {}) {
    super(knex, table, options)
    this.parentField = options.parentField ?? 'parent_id'
  }

  // ---------------------------------------------------------------------------
  // ITreeRepository methods
  // ---------------------------------------------------------------------------

  /**
   * Get tree nodes as a flat BFS-ordered list.
   *
   * @param rootId  - Optional root node ID. If provided, returns the subtree
   *                  rooted at that node (including the root itself).
   * @param maxDepth - Maximum depth to traverse (0 = root only, 1 = root +
   *                   direct children, etc.). Defaults to unlimited.
   * @returns Flat array in BFS order. Empty array if rootId is provided but
   *          does not exist.
   */
  async getTree(rootId?: string, maxDepth?: number): Promise<T[]> {
    const result: T[] = []
    const visited = new Set<string>()
    const depthLimit = maxDepth ?? Infinity

    // Determine the initial level
    let currentLevel: T[]
    if (rootId !== undefined) {
      const root = await this.findById(rootId)
      if (!root) return []
      currentLevel = [root]
    } else {
      currentLevel = await this.findRoots()
    }

    let depth = 0

    while (currentLevel.length > 0 && depth <= depthLimit) {
      const nextLevel: T[] = []

      for (const node of currentLevel) {
        const nodeId = node['id'] as string
        if (visited.has(nodeId)) continue
        visited.add(nodeId)
        result.push(node)

        if (depth < depthLimit) {
          const children = await this.findChildren(nodeId)
          for (const child of children) {
            const childId = child['id'] as string
            if (!visited.has(childId)) {
              nextLevel.push(child)
            }
          }
        }
      }

      currentLevel = nextLevel
      depth++
    }

    return result
  }

  /**
   * Get all root nodes (nodes with no parent).
   */
  async findRoots(): Promise<T[]> {
    return this.findChildren(null)
  }

  /**
   * Get direct children of a node.
   *
   * @param parentId - Parent node ID, or null to get root nodes.
   */
  async findChildren(parentId: string | null): Promise<T[]> {
    let qb: ReturnType<Knex['queryBuilder']>

    if (parentId === null) {
      qb = this.knex(this.table).whereNull(this.parentField)
    } else {
      qb = this.knex(this.table).where(this.parentField, parentId)
    }

    return qb as unknown as Promise<T[]>
  }

  /**
   * Get all ancestors of a node, ordered from immediate parent to root.
   *
   * @param id - Node ID to get ancestors for.
   * @returns Array of ancestors from leaf-to-root (immediate parent first).
   */
  async getAncestors(id: string): Promise<T[]> {
    const ancestors: T[] = []
    const visited = new Set<string>()

    let current = await this.findById(id)
    if (!current) return []

    visited.add(id)

    while (current) {
      const parentId = current[this.parentField] as string | null | undefined

      if (!parentId) break

      // Cycle defence
      if (visited.has(parentId)) break
      visited.add(parentId)

      const parent = await this.findById(parentId)
      if (!parent) break

      ancestors.push(parent)
      current = parent
    }

    return ancestors
  }

  /**
   * Get all descendants of a node via BFS. Does not include the node itself.
   *
   * @param id - Node ID to get descendants for.
   */
  async getDescendants(id: string): Promise<T[]> {
    const descendants: T[] = []
    const visited = new Set<string>([id])
    const queue = [id]

    while (queue.length > 0) {
      const currentId = queue.shift()!
      const children = await this.findChildren(currentId)

      for (const child of children) {
        const childId = child['id'] as string
        if (!visited.has(childId)) {
          visited.add(childId)
          descendants.push(child)
          queue.push(childId)
        }
      }
    }

    return descendants
  }

  /**
   * Move a node to a new parent with cycle detection.
   *
   * @param id          - Node ID to move.
   * @param newParentId - New parent ID, or null to make the node a root.
   * @returns Updated node.
   * @throws Error if the move would create a cycle, newParentId equals id,
   *         or newParentId does not exist.
   */
  async move(id: string, newParentId: string | null): Promise<T> {
    if (newParentId !== null) {
      // Self-reference check
      if (newParentId === id) {
        throw new Error('Cannot move node under itself')
      }

      // Target parent must exist
      const parent = await this.findById(newParentId)
      if (!parent) {
        throw new Error('Parent not found')
      }

      // Cycle check: newParentId must not be a descendant of id
      const descendants = await this.getDescendants(id)
      if (descendants.some((d) => d['id'] === newParentId)) {
        throw new Error('Cannot move under own descendant')
      }
    }

    try {
      await this.knex(this.table)
        .where('id', id)
        .update({ [this.parentField]: newParentId })
    } catch (e) {
      handleDbError(e)
    }

    return (await this.findById(id)) as T
  }
}
